import React, {useCallback} from 'react';

import {TranslatedSpecialityField} from '@lexian/api/casus-api';
import {useMasterdata} from '_/hooks/useMasterdata';
import i18n from '_/i18n';
import {EFAutocomplete, EFAutocompleteProps} from '_/utilities/form';
import {
  getActiveOptions,
  getOptions,
  TranslatedActivatableItem,
} from '_/utilities/masterdata.utils';

import {
  useActiveFilters,
  UseActiveFiltersProps,
} from './ActiveFilter/ActiveFilter.hooks';
import {EFTagsAutocomplete} from './ETagsAutocomplete';

type CommonProps = Omit<EFAutocompleteProps, 'options'> & {multiple?: boolean};

type FilterableProps = {filterable: true} & CommonProps &
  Pick<UseActiveFiltersProps, 'onFilterChange' | 'persistenceKey'>;

type NonFilterableProps = {filterable: false} & CommonProps;

type Props = (NonFilterableProps | FilterableProps) & {
  onUserChange?: (
    specialityField: TranslatedSpecialityField | undefined
  ) => void;
};

export function SpecialityFieldAutocomplete({
  label = i18n.t('common:labels.specialityField'),
  onUserChange,
  filterable,
  multiple = false,
  ...otherProps
}: Props): React.ReactElement {
  const {specialityFields} = useMasterdata();

  const onChangeHandler = useCallback(
    (
      e: React.ChangeEvent<any>,
      originalEventHandler: React.ChangeEventHandler
    ) => {
      if (onUserChange) {
        const selectedSpecialityField = specialityFields.find(
          (specialityField) => specialityField.id === e.target.value
        );
        onUserChange(selectedSpecialityField);
      }
      originalEventHandler(e);
    },
    [onUserChange, specialityFields]
  );

  if (filterable && 'persistenceKey' in otherProps) {
    return (
      <FilterableSpecialityFieldAutocomplete
        {...otherProps}
        filterable={filterable}
        label={label}
        multiple={multiple}
        specialityFields={specialityFields}
      />
    );
  }

  const options = getActiveOptions(specialityFields);

  return (
    <>
      {multiple ? (
        <EFTagsAutocomplete
          {...otherProps}
          customOnChange={onChangeHandler}
          label={label}
          options={options}
          noOptionFiltering
        />
      ) : (
        <EFAutocomplete
          {...otherProps}
          customOnChange={onChangeHandler}
          label={label}
          options={options}
          noOptionFiltering
        />
      )}
    </>
  );
}

function FilterableSpecialityFieldAutocomplete({
  multiple = false,
  specialityFields,
  onFilterChange,
  persistenceKey,
  ...otherProps
}: FilterableProps & {
  specialityFields: TranslatedActivatableItem[];
}) {
  const {
    activeOptions,
    showActiveFiltersDialog,
    activeFiltersDialog,
    optionFilterInfo,
  } = useActiveFilters({
    options: specialityFields,
    persistenceKey: `specialityFieldAutocomplete.searchFilters.${persistenceKey}`,
    onFilterChange,
  });
  const options = getOptions(activeOptions);

  return (
    <>
      {multiple ? (
        <EFTagsAutocomplete
          {...otherProps}
          optionFilterInfo={optionFilterInfo}
          options={options}
          noOptionFiltering
          onFilterClick={showActiveFiltersDialog}
        />
      ) : (
        <EFAutocomplete
          {...otherProps}
          optionFilterInfo={optionFilterInfo}
          options={options}
          noOptionFiltering
          onFilterClick={showActiveFiltersDialog}
        />
      )}
      {activeFiltersDialog}
    </>
  );
}
